import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material';

import { useEventBus } from '../../../../../hooks/use-event-bus';

import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { ContactApi } from '../../../../../services/api-client/contact-api';
import FormsService from '../../../../../services/forms-service/forms-service';
import { PacketForms, PatientDto, SpecialtyIndexes } from '../../../../../services/forms-service/forms-service-types';
import { checkPatientData } from '../helpers';
import { CenterContent, DialogAddForm } from '../styles';
import FormsTab from './forms-tab';
import PacketsTab from './packets-tab';

export enum TabsTypes {
  Forms = 'forms',
  Packets = 'packets'
}

export default function ModalAddForm() {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [patient, setPatient] = useState<PatientDto | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabsTypes>(TabsTypes.Forms);
  const [specialties, setSpecialties] = useState<SpecialtyIndexes[]>([]);

  const [selectedForms, setSelectedForms] = useState<PacketForms[]>([]);
  const [selectedPackets, setSelectedPackets] = useState<PacketForms[]>([]);

  const { showSuccess, showError } = useNotification();

  const { listen, publish } = useEventBus();
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();

  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  const initiateAddForm = useCallback(
    async (locationId: number, patientId?: number, patientData?: PatientDto) => {
      try {
        setIsLoading(true);
        const patient = patientData || (await contactApi.getContactDetailsById(patientId!));
        setPatient(patient);
        setLocationId(locationId);
        setSelectedForms([]);
        setSelectedPackets([]);

        checkPatientData(patient, publish, 'or');
      } finally {
        setIsLoading(false);
      }
    },
    [contactApi, publish]
  );

  useEffect(() => {
    const getFormAssignmentsIndex = async () => {
      const response = await formsService.getFormAssignmentsIndex();
      setSpecialties(response.specialties);
    };

    getFormAssignmentsIndex();
  }, [formsService]);

  useEffect(() => {
    return listen('formsDialog', async (event: any) => {
      const data = event.data as { type: string; locationId: number; patientId?: number; patient?: PatientDto };

      if (data.type === 'add-form' && (data.patientId || data.patient) && data.locationId) {
        setOpen(true);
        try {
          await initiateAddForm(data.locationId, data.patientId, data.patient);
        } catch (error) {
          console.error(error);
          setOpen(false);
        }
      }
    });
  }, [listen]);

  const handleClose = () => {
    setOpen(false);
  };

  const submit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setOpen(false);

    try {
      const formIds = selectedForms.map((form) => form.formId!);
      const packetIds = selectedPackets.map((packet) => packet.formId!);

      await formsService.assignForms({
        formIds,
        packetIds,
        contact: {
          id: patient!.id!,
          firstName: patient!.firstName!,
          lastName: patient!.lastName!,
          email: patient!.email!,
          dateOfBirth: patient!.dateOfBirth!,
          locationId: locationId!
        }
      });
      publish({ messageType: 'forms', data: { type: 'reload' } });
      showSuccess('Forms have been assigned');
    } catch {
      showError('Failed to assign forms');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogAddForm open={open} onClose={handleClose}>
      {isLoading ? (
        <>
          <DialogTitle>Add Forms/Packets</DialogTitle>
          <DialogContent>
            <CenterContent>
              <CircularProgress />
            </CenterContent>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle>
            Add Forms/Packets for {patient?.firstName || ''} {patient?.lastName || ''}
          </DialogTitle>
          <DialogContent>
            Select all the new forms/packets that you would like to include
            <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
              {Object.values(TabsTypes).map((tab) => (
                <Tab key={tab} value={tab} label={tab} />
              ))}
            </Tabs>
            <br />
            <FormsTab
              specialties={specialties}
              visible={selectedTab === TabsTypes.Forms}
              locationId={locationId}
              selectedForms={selectedForms}
              setSelectedForms={setSelectedForms}
            />
            <PacketsTab
              visible={selectedTab === TabsTypes.Packets}
              locationId={locationId}
              selectedPackets={selectedPackets}
              setSelectedPackets={setSelectedPackets}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button variant="contained" onClick={submit}>
              Save Changes
            </Button>
          </DialogActions>
        </>
      )}
    </DialogAddForm>
  );
}
